import React, { useState } from 'react';
import { login } from '../../api';

const AdminLogin = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [remember, setRemember] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(username, password);
      if (data.require2FA) {
        setRequire2FA(true);
        setTempToken(data.tempToken);
      } else {
        localStorage.setItem('adminToken', data.token);
        onLoginSuccess();
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    }
    setLoading(false);
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(username, '', tempToken, totpCode, remember);
      localStorage.setItem('adminToken', data.token);
      onLoginSuccess();
    } catch (err) {
      setError(err.message || '2FA verification failed');
    }
    setLoading(false);
  };

  return (
    <div className="admin-login-page">
      <div className="login-card">
        <div className="login-header">
          <span className="login-icon">{require2FA ? '🔐' : '🔒'}</span>
          <h2>{require2FA ? 'Two-Factor Authentication' : 'Admin Login'}</h2>
          <p>{require2FA ? 'Enter the code from your authenticator app.' : 'Secure access for prospect management'}</p>
        </div>

        {!require2FA ? (
          <form onSubmit={handleSubmit} className="login-form">
            {error && <div className="login-error">{error}</div>}
            <div className="form-group">
              <label>Username</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handle2FA} className="login-form">
            {error && <div className="login-error">{error}</div>}
            <div className="form-group">
              <label>6-Digit Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                placeholder="000000"
                className="totp-input"
              />
            </div>
            <div className="remember-row">
              <label className="remember-label">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                <span>Remember this device for 30 days</span>
              </label>
            </div>
            <button type="submit" className="btn-login" disabled={loading || totpCode.length !== 6}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button type="button" className="btn-back" onClick={() => { setRequire2FA(false); setError(''); }}>
              Back to login
            </button>
          </form>
        )}
      </div>

      <style>{`
        .admin-login-page {
          display: flex; align-items: center; justify-content: center;
          min-height: calc(100vh - 65px); padding: 20px;
        }
        .login-card {
          background: #1a1a2e; border: 1px solid #2a2a4a;
          border-radius: 16px; padding: 40px; width: 100%; max-width: 400px;
        }
        .login-header { text-align: center; margin-bottom: 28px; }
        .login-icon { font-size: 2.5rem; display: block; margin-bottom: 12px; }
        .login-header h2 { color: #fff; font-size: 1.5rem; margin-bottom: 6px; }
        .login-header p { color: #8888aa; font-size: 0.85rem; }
        .login-error {
          background: rgba(255,68,68,0.15); border: 1px solid rgba(255,68,68,0.3);
          color: #ff6666; padding: 10px 14px; border-radius: 8px;
          margin-bottom: 16px; font-size: 0.85rem;
        }
        .form-group { margin-bottom: 16px; }
        .form-group label {
          display: block; color: #aaaacc; font-size: 0.8rem;
          font-weight: 600; margin-bottom: 6px;
        }
        .form-group input {
          width: 100%; padding: 10px 14px; border-radius: 10px;
          border: 1px solid #3a3a5a; background: #12122a; color: #e0e0e0;
          font-size: 0.95rem; outline: none; transition: border-color 0.2s;
        }
        .form-group input:focus { border-color: #f7971e; }
        .totp-input {
          text-align: center; font-size: 1.5rem !important;
          letter-spacing: 8px; font-family: monospace;
        }
        .btn-login {
          width: 100%; padding: 12px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #f7971e, #ffd200); color: #1a1a2e;
          font-weight: 700; font-size: 1rem; cursor: pointer; margin-top: 8px;
          transition: opacity 0.2s;
        }
        .btn-login:hover { opacity: 0.9; }
        .btn-login:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-back {
          width: 100%; padding: 10px; margin-top: 10px; border-radius: 10px;
          border: 1px solid #3a3a5a; background: transparent; color: #8888aa;
          font-size: 0.85rem; cursor: pointer; transition: color 0.2s;
        }
        .btn-back:hover { color: #fff; }
        .remember-row { margin: 12px 0; }
        .remember-label {
          display: flex; align-items: center; gap: 8px;
          color: #8888aa; font-size: 0.85rem; cursor: pointer;
        }
        .remember-label input[type="checkbox"] { accent-color: #f7971e; }
      `}</style>
    </div>
  );
};

export default AdminLogin;
